//REACT
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

//YUP
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

//MUI
import Box from "@mui/material/Box";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import LoadingButton from "@mui/lab/LoadingButton";
import SaveIcon from "@mui/icons-material/Save";
import {
  Collapse,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";

//COMPONENTS
import { CustomScrollbar } from "../../customScrollbar/CustomScrollbar";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { DatePicker } from "@mui/lab";

const schema = yup.object().shape({
  carouselImagesList: yup.array().of(
    yup.object().shape({
      url: yup.string(),
    })
  ),
  completeMissionDescription: yup.string(),
  completeMissionImage: yup.object().shape({
    url: yup.string(),
  }),
  eventButtonText: yup.string(),
  eventButtonUrl: yup.string(),
  eventCode: yup.string(),
  eventDescription: yup.string(),
  eventEndDate: yup.date(),
  eventName: yup.string(),
  eventPublishDate: yup.date(),
  eventSecondaryButtonText: yup.string(),
  eventSecondaryButtonUrl: yup.string(),
  eventStartDate: yup.date(),
  eventTag: yup.string(),
  eventType: yup.string(),
  id: yup.string(),
  image: yup.object().shape({
    url: yup.string(),
  }),
  initiativeDescription: yup.string(),
  longCompleteDescription: yup.string(),

  title: yup.string(),
  userEnabled: yup.boolean(),
  partnerDetails: yup.object().shape({
    buttonText: yup.string(),
    buttonUrl: yup.string(),
    code: yup.string(),
    crossMarketingComponent: yup.object().shape({
      buttonText: yup.string(),
      buttonUrl: yup.string(),
      code: yup.string(),
      descriptionImage: yup.string(),
      firstDescription: yup.string(),
      image: yup.object().shape({
        url: yup.string(),
      }),
      secondaryDescription: yup.string(),
      tag: yup.string(),
    }),
  }),
});

const Form = ({ mode = "create", handleSave, currentProduct }) => {
  const isEdit = mode === "edit";
  const { id, dataCreation, ...event } = currentProduct;

  const todayDate = new Date().toISOString().split("T")[0];

  const defaultValues = {
    carouselImagesList: [
      {
        url: "",
      },
      {
        url: "",
      },
    ],
    completeMissionDescription: "",
    completeMissionImage: {
      url: "",
    },
    eventButtonText: "",
    eventButtonUrl: "",
    eventCode: "",
    eventDescription: "",
    eventEndDate: todayDate,
    eventName: "",
    eventPublishDate: todayDate,
    eventSecondaryButtonText: "",
    eventSecondaryButtonUrl: "",
    eventStartDate: todayDate,
    eventTag: "",
    eventType: "",
    id: "",
    image: {
      url: "",
    },
    initiativeDescription: "",
    longCompleteDescription: "",
    partnerDetails: {
      buttonText: "",
      buttonUrl: "",
      code: "",
      crossMarketingComponent: {
        buttonText: "",
        buttonUrl: "",
        code: "",
        descriptionImage: "",
        firstDescription: "",
        image: {
          url: "",
        },
        secondaryDescription: "",
        tag: "",
      },
    },
    ...event,
  };

  const form = useForm({
    defaultValues,
    mode: "onBlur",
    resolver: yupResolver(schema),
  });

  const { register, handleSubmit, formState, reset } = form;
  const { errors, isValid, isDirty } = formState;

  useEffect(() => {
    reset(defaultValues);
  }, [currentProduct]);

  const onSubmit = (data) => {
    handleSave(data);
  };

  //NESTED LIST
  const [open, setOpen] = useState(false);
  const handleClick = () => setOpen(!open);

  return (
    <CustomScrollbar>
      <Box
        id="eventForm"
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        maxWidth="95%"
        maxHeight="35vw"
      >
        <Grid2 container spacing={3}>
          <Grid2 xs={12}>
            <Typography variant="h3" color="primary">
              Event {isEdit ? "details" : "creation"}
            </Typography>
          </Grid2>

          {/* EVENT SECTION */}

          <Grid2 xs={12}>
            <TextField
              InputLabelProps={{ shrink: true }}
              label="Event code"
              color="primary"
              fullWidth
              error={!!errors.eventCode}
              helperText={errors.eventCode?.message}
              {...register("eventCode")}
            />
          </Grid2>

          <Grid2 xs={12}>
            <TextField
              InputLabelProps={{ shrink: true }}
              label="Event name"
              color="primary"
              fullWidth
              error={!!errors.eventName}
              helperText={errors.eventName?.message}
              {...register("eventName")}
            />
          </Grid2>

          <Grid2 xs={12}>
            <TextField
              InputLabelProps={{ shrink: true }}
              label="Event button text"
              color="primary"
              fullWidth
              error={!!errors.eventButtonText}
              helperText={errors.eventButtonText?.message}
              {...register("eventButtonText")}
            />
          </Grid2>

          <Grid2 xs={12}>
            <TextField
              InputLabelProps={{ shrink: true }}
              label="Event button Url"
              color="primary"
              fullWidth
              error={!!errors.eventButtonUrl}
              helperText={errors.eventButtonUrl?.message}
              {...register("eventButtonUrl")}
            />
          </Grid2>

          <Grid2 xs={12}>
            <TextField
              InputLabelProps={{ shrink: true }}
              label="Event code"
              color="primary"
              fullWidth
              error={!!errors.eventCode}
              helperText={errors.eventCode?.message}
              {...register("eventCode")}
            />
          </Grid2>
          <Grid2 xs={12}>
            <TextField
              InputLabelProps={{ shrink: true }}
              label="Event description"
              color="primary"
              fullWidth
              error={!!errors.eventDescription}
              helperText={errors.eventDescription?.message}
              {...register("eventDescription")}
            />
          </Grid2>

          <Grid2 item xs={12}>
            <TextField
              InputLabelProps={{ shrink: true }}
              label="Event end date"
              type="date"
              color="primary"
              fullWidth
              error={!!errors.eventEndDate}
              helperText={errors.eventEndDate?.message}
              {...register("eventEndDate")}
            />
            <DatePicker />
          </Grid2>

          <Grid2 xs={12}>
            <TextField
              InputLabelProps={{ shrink: true }}
              label="Event secondary button text"
              color="primary"
              fullWidth
              error={!!errors.eventSecondaryButtonText}
              helperText={errors.eventSecondaryButtonText?.message}
              {...register("eventSecondaryButtonText")}
            />
          </Grid2>

          <Grid2 xs={12}>
            <TextField
              InputLabelProps={{ shrink: true }}
              label="Event secondary button url"
              color="primary"
              fullWidth
              error={!!errors.eventSecondaryButtonUrl}
              helperText={errors.eventSecondaryButtonUrl?.message}
              {...register("eventSecondaryButtonUrl")}
            />
          </Grid2>

          <Grid2 item xs={12}>
            <TextField
              InputLabelProps={{ shrink: true }}
              label="Event start date"
              type="date"
              color="primary"
              fullWidth
              error={!!errors.eventStartDate}
              helperText={errors.eventStartDate?.message}
              {...register("eventStartDate")}
            />
            <DatePicker />
          </Grid2>

          <Grid2 xs={12}>
            <TextField
              InputLabelProps={{ shrink: true }}
              label="Event Tag"
              color="primary"
              fullWidth
              error={!!errors.eventTag}
              helperText={errors.eventTag?.message}
              {...register("eventTag")}
            />
          </Grid2>

          <Grid2 xs={12}>
            <TextField
              InputLabelProps={{ shrink: true }}
              label="Event type"
              color="primary"
              fullWidth
              error={!!errors.eventType}
              helperText={errors.eventType?.message}
              {...register("eventType")}
            />
          </Grid2>

          <Grid2 xs={12}>
            <TextField
              InputLabelProps={{ shrink: true }}
              label="Id"
              color="primary"
              fullWidth
              error={!!errors.id}
              helperText={errors.id?.message}
              {...register("id")}
            />
          </Grid2>

          {/* (event)image */}
          <Grid2 xs={12}>
            <List
              sx={{ bgcolor: "background.paper" }}
              component="nav"
              aria-labelledby="nested-list-subheader"
            >
              <ListItemButton
                onClick={handleClick}
                sx={{
                  border: 1,
                  borderColor: "lightgrey",
                  boxShadow: 1,
                  borderRadius: "5px",
                }}
              >
                <ListItemText primary="Event image" />
                {open ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
              <Collapse in={open} timeout="auto" unmountOnExit>
                <List
                  component="div"
                  disablePadding
                  sx={{
                    border: 1,
                    borderColor: "lightgrey",
                    borderRadius: "5px",
                  }}
                >
                  <ListItemButton sx={{ pl: 2 }}>
                    <Grid2 xs={12}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        color="primary"
                        fullWidth
                        label="Image URL"
                        value={form.watch("image.url")}
                        {...register("image.url")}
                        sx={{ marginTop: "10px", marginBottom: "10px" }}
                      />
                    </Grid2>
                  </ListItemButton>
                </List>
              </Collapse>
            </List>
          </Grid2>

          {/* CAROUSEL IMAGE LIST SECTION*/}

          <Grid2 xs={12}>
            <List
              sx={{ bgcolor: "background.paper" }}
              component="nav"
              aria-labelledby="nested-list-subheader"
            >
              <ListItemButton
                onClick={handleClick}
                sx={{
                  border: 1,
                  borderColor: "lightgrey",
                  boxShadow: 1,
                  borderRadius: "5px",
                }}
              >
                <ListItemText primary="Carousel Images" />
                {open ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
              <Collapse in={open} timeout="auto" unmountOnExit>
                <List
                  component="div"
                  disablePadding
                  sx={{
                    border: 1,
                    borderColor: "lightgrey",
                    borderRadius: "5px",
                  }}
                >
                  <ListItemButton sx={{ pl: 2 }}>
                    <Grid2 xs={12}>
                      {defaultValues.carouselImagesList.map((item, index) => (
                        <Grid2 item xs={12} key={index}>
                          <TextField
                            InputLabelProps={{ shrink: true }}
                            label={`Carousel Image ${index + 1}`}
                            color="primary"
                            fullWidth
                            defaultValue={item.url}
                            {...register(`carouselImagesList.${index}.url`)}
                          />
                        </Grid2>
                      ))}
                    </Grid2>
                  </ListItemButton>
                </List>
              </Collapse>
            </List>
          </Grid2>

          {/* MISSION SECTION*/}
          <Grid2 xs={12}>
            <TextField
              InputLabelProps={{ shrink: true }}
              label="Completed Mission Description"
              color="primary"
              fullWidth
              error={!!errors.completeMissionDescription}
              helperText={errors.completeMissionDescription?.message}
              {...register("completeMissionDescription")}
            />
          </Grid2>

          {/* completeMissionImage */}
          <Grid2 xs={12}>
            <List
              sx={{ bgcolor: "background.paper" }}
              component="nav"
              aria-labelledby="nested-list-subheader"
            >
              <ListItemButton
                onClick={handleClick}
                sx={{
                  border: 1,
                  borderColor: "lightgrey",
                  boxShadow: 1,
                  borderRadius: "5px",
                }}
              >
                <ListItemText primary="Completed Mission Image" />
                {open ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
              <Collapse in={open} timeout="auto" unmountOnExit>
                <List
                  component="div"
                  disablePadding
                  sx={{
                    border: 1,
                    borderColor: "lightgrey",
                    borderRadius: "5px",
                  }}
                >
                  <ListItemButton sx={{ pl: 2 }}>
                    <Grid2 xs={12}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        color="primary"
                        fullWidth
                        label="Image URL"
                        value={form.watch("completeMissionImage.url")}
                        {...register("completeMissionImage.url")}
                        sx={{ marginTop: "10px", marginBottom: "10px" }}
                      />
                    </Grid2>
                  </ListItemButton>
                </List>
              </Collapse>
            </List>
          </Grid2>

          <Grid2 xs={12}>
            <TextField
              InputLabelProps={{ shrink: true }}
              label="Initiative description"
              color="primary"
              fullWidth
              error={!!errors.initiativeDescription}
              helperText={errors.initiativeDescription?.message}
              {...register("initiativeDescription")}
            />
          </Grid2>

          <Grid2 xs={12}>
            <TextField
              InputLabelProps={{ shrink: true }}
              label="Long complete description"
              color="primary"
              fullWidth
              error={!!errors.longCompleteDescription}
              helperText={errors.longCompleteDescription?.message}
              {...register("longCompleteDescription")}
            />
          </Grid2>

          {/* partnerDetails */}

          {/* <Grid2 xs={12}>
            <List
              sx={{ bgcolor: "background.paper" }}
              component="nav"
              aria-labelledby="nested-list-subheader"
            >
              <ListItemButton
                onClick={handleClick}
                sx={{
                  border: 1,
                  borderColor: "lightgrey",
                  boxShadow: 1,
                  borderRadius: "5px",
                }}
              >
                <ListItemText primary="Partner Details" />
                {open ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
              <Collapse in={open} timeout="auto" unmountOnExit>
                <List
                  component="div"
                  disablePadding
                  sx={{
                    border: 1,
                    borderColor: "lightgrey",
                    borderRadius: "5px",
                  }}
                >
                  <ListItemButton sx={{ pl: 2 }}>
                    <Grid2 xs={12}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        label="Button Text"
                        color="primary"
                        fullWidth
                        error={!!errors.partnerDetails.buttonText}
                        helperText={errors.partnerDetails?.buttonText?.message}
                        {...register("partnerDetails?.buttonText")}
                      />
                    </Grid2>
                    <Grid2 xs={12}>
                      <TextField
                      InputLabelProps={{ shrink: true }}
                        label="Button Url"
                        color="primary"
                        fullWidth
                        error={!!errors.partnerDetails.buttonUrl}
                        helperText={errors.partnerDetails?.buttonUrl?.message}
                        {...register("partnerDetails?.buttonUrl")}
                      />
                    </Grid2>
                    <Grid2 xs={12}>
                      <TextField
                      InputLabelProps={{ shrink: true }}
                        label="Code"
                        color="primary"
                        fullWidth
                        error={!!errors.partnerDetails.code}
                        helperText={errors.partnerDetails?.code?.message}
                        {...register("partnerDetails?.code")}
                      />
                    </Grid2> 

                    <Grid2 xs={12}>
                      <List
                        sx={{ bgcolor: "background.paper" }}
                        component="nav"
                        aria-labelledby="nested-list-subheader"
                      >
                        <ListItemButton
                          onClick={handleClick}
                          sx={{
                            border: 1,
                            borderColor: "lightgrey",
                            boxShadow: 1,
                            borderRadius: "5px",
                          }}
                        >
                          <ListItemText primary="Cross Marketing Component" />
                          {open ? <ExpandLess /> : <ExpandMore />}
                        </ListItemButton>
                        <Collapse in={open} timeout="auto" unmountOnExit>
                          <List
                            component="div"
                            disablePadding
                            sx={{
                              border: 1,
                              borderColor: "lightgrey",
                              borderRadius: "5px",
                            }}
                          >
                            <ListItemButton sx={{ pl: 2 }}>
                              <Grid2 xs={12}>
                                <TextField
                                InputLabelProps={{ shrink: true }}
                                  label="CMC Button Text"
                                  color="primary"
                                  fullWidth
                                  error={
                                    !!errors.partnerDetails
                                      .crossMarketingComponent.buttonText
                                  }
                                  helperText={
                                    errors.partnerDetails
                                      .crossMarketingComponent.buttonText
                                      ?.message
                                  }
                                  {...register(
                                    "partnerDetails.crossMarketingComponent.buttonText"
                                  )}
                                />
                              </Grid2>
                              <Grid2 xs={12}>
                                <TextField
                                InputLabelProps={{ shrink: true }}
                                  label="CMC Button Url"
                                  color="primary"
                                  fullWidth
                                  error={
                                    !!errors.partnerDetails
                                      .crossMarketingComponent.buttonUrl
                                  }
                                  helperText={
                                    errors.partnerDetails
                                      .crossMarketingComponent.buttonUrl
                                      ?.message
                                  }
                                  {...register(
                                    "partnerDetails.crossMarketingComponent.buttonUrl"
                                  )}
                                />
                              </Grid2>
                              <Grid2 xs={12}>
                                <TextField
                                InputLabelProps={{ shrink: true }}
                                  label="CMC Code"
                                  color="primary"
                                  fullWidth
                                  error={
                                    !!errors.partnerDetails
                                      .crossMarketingComponent.code
                                  }
                                  helperText={
                                    errors.partnerDetails
                                      .crossMarketingComponent.code?.message
                                  }
                                  {...register(
                                    "partnerDetails.crossMarketingComponent.code"
                                  )}
                                />
                              </Grid2>

                              <Grid2 xs={12}>
                                <TextField
                                InputLabelProps={{ shrink: true }}
                                  label="CMC description image"
                                  color="primary"
                                  fullWidth
                                  error={
                                    !!errors.partnerDetails
                                      .crossMarketingComponent.descriptionImage
                                  }
                                  helperText={
                                    errors.partnerDetails
                                      .crossMarketingComponent.descriptionImage
                                      ?.message
                                  }
                                  {...register(
                                    "partnerDetails.crossMarketingComponent.descriptionImage"
                                  )}
                                />
                              </Grid2>
                              <Grid2 xs={12}>
                                <TextField
                                InputLabelProps={{ shrink: true }}
                                  label="CMC first description"
                                  color="primary"
                                  fullWidth
                                  error={
                                    !!errors.partnerDetails
                                      .crossMarketingComponent.firstDescription
                                  }
                                  helperText={
                                    errors.partnerDetails
                                      .crossMarketingComponent.firstDescription
                                      ?.message
                                  }
                                  {...register(
                                    "partnerDetails.crossMarketingComponent.firstDescription"
                                  )}
                                />
                              </Grid2>

                              {/*partner details crossmarketing component image 
                              
                              <Grid2 xs={12}>
                                <List
                                  sx={{ bgcolor: "background.paper" }}
                                  component="nav"
                                  aria-labelledby="nested-list-subheader"
                                >
                                  <ListItemButton
                                    onClick={handleClick}
                                    sx={{
                                      border: 1,
                                      borderColor: "lightgrey",
                                      boxShadow: 1,
                                      borderRadius: "5px",
                                    }}
                                  >
                                    <ListItemText primary="Partner Crossmarketing Image" />
                                    {open ? <ExpandLess /> : <ExpandMore />}
                                  </ListItemButton>
                                  <Collapse
                                    in={open}
                                    timeout="auto"
                                    unmountOnExit
                                  >
                                    <List
                                      component="div"
                                      disablePadding
                                      sx={{
                                        border: 1,
                                        borderColor: "lightgrey",
                                        borderRadius: "5px",
                                      }}
                                    >
                                      <ListItemButton sx={{ pl: 2 }}>
                                        <Grid2 xs={12}>
                                          <TextField
                                          InputLabelProps={{ shrink: true }}
                                            color="primary"
                                            fullWidth
                                            label="Image URL"
                                            value={form.watch(
                                              "partnerDetails.crossMarketingComponent.image.url"
                                            )}
                                            {...register(
                                              "partnerDetails.crossMarketingComponent.image.url"
                                            )}
                                            sx={{
                                              marginTop: "10px",
                                              marginBottom: "10px",
                                            }}
                                          />
                                        </Grid2>
                                      </ListItemButton>
                                    </List>
                                  </Collapse>
                                </List>
                              </Grid2>

                              <Grid2 xs={12}>
                                <TextField
                                InputLabelProps={{ shrink: true }}
                                  label="CMC secondary description"
                                  color="primary"
                                  fullWidth
                                  error={
                                    !!errors.partnerDetails
                                      .crossMarketingComponent
                                      .secondaryDescription
                                  }
                                  helperText={
                                    errors.partnerDetails
                                      .crossMarketingComponent
                                      .secondaryDescription?.message
                                  }
                                  {...register(
                                    "partnerDetails.crossMarketingComponent.secondaryDescription"
                                  )}
                                />
                              </Grid2>

                              <Grid2 xs={12}>
                                <TextField
                                InputLabelProps={{ shrink: true }}
                                  label="CMC tag"
                                  color="primary"
                                  fullWidth
                                  error={
                                    !!errors.partnerDetails
                                      .crossMarketingComponent.tag
                                  }
                                  helperText={
                                    errors.partnerDetails
                                      .crossMarketingComponent.tag?.message
                                  }
                                  {...register(
                                    "partnerDetails.crossMarketingComponent.tag"
                                  )}
                                />
                              </Grid2>
                            </ListItemButton>
                          </List>
                        </Collapse>
                      </List>
                    </Grid2>
                  </ListItemButton>
                </List>
              </Collapse>
            </List>
          </Grid2>  */}

          <Grid2 xs={12}>
            <Tooltip arrow title={(false && "a") || ""}>
              <LoadingButton
                type="submit"
                loading={false}
                loadingPosition="start"
                startIcon={<SaveIcon />}
                variant="contained"
                sx={{
                  boxShadow: "none",
                  padding: "5px 12px 4px 12px",
                }}
                color="primary"
                onClick={() => {}}
                // disabled={isDirty && !isValid}
              >
                Save
              </LoadingButton>
            </Tooltip>
          </Grid2>
        </Grid2>
      </Box>
    </CustomScrollbar>
  );
};

export default Form;
